import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { CALENDLY_LINK, CONTACT_PHONE } from '../../utils/constants';
import CaseStudyCard from './components/CaseStudyCard';
import FilterPanel from './components/FilterPanel';
import CaseStudyModal from './components/CaseStudyModal';
import StatsOverview from './components/StatsOverview';
import SearchBar from './components/SearchBar';

const CaseStudies = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    industry: [],
    serviceType: [],
    companySize: [],
    location: []
  });

  // Mock case studies data
  const caseStudies = [
    {
      id: 1,
      title: "Tech Startup Acquisition Valuation",
      industry: "Technology",
      serviceType: "Transaction Support",
      companySize: "Startup",
      location: "Dubai, UAE",
      year: "2024",
      duration: "6 weeks",
      challenge: "Fast-growing fintech startup required independent valuation for Series B funding round with international investors.",
      detailedChallenge: `A Dubai - based fintech startup specializing in digital payment solutions needed an independent valuation for their Series B funding round.The company had experienced rapid growth but lacked traditional financial metrics, making valuation challenging.International investors required IVS - compliant valuation with detailed methodology and assumptions.\n\nKey challenges included limited historical data, high growth volatility, regulatory uncertainty in the fintech space, and the need to balance growth potential with risk factors specific to the GCC market.`,
      uniqueConsiderations: [
        "Applied DCF methodology with scenario analysis for high-growth technology company",
        "Incorporated GCC fintech regulatory landscape and market penetration rates",
        "Utilized comparable company analysis from regional and international fintech peers",
        "Addressed currency risk and regional market dynamics in valuation assumptions"
      ],
      outcome: {
        valueRange: "$45-52M",
        timeline: "2 weeks early",
        satisfaction: "98%"
      },
      clientTestimonial: "The valuation report was comprehensive and well-received by our international investors. The methodology was sound and the regional expertise was invaluable.",
      clientRole: "Chief Financial Officer",
      clientCompany: "Regional Fintech Leader"
    },
    {
      id: 2,
      title: "Family Business Succession Planning",
      industry: "Family Business",
      serviceType: "Business Valuation",
      companySize: "SME",
      location: "Riyadh, KSA",
      year: "2024",
      duration: "10 weeks",
      challenge: "Multi-generational family business required valuation for succession planning and estate tax purposes.",
      detailedChallenge: `A well - established family trading business with operations across the GCC needed comprehensive valuation for succession planning.The business had complex ownership structures, multiple business lines, and significant real estate holdings.The valuation needed to address Sharia compliance requirements and family governance considerations.\n\nComplexities included intercompany transactions, related party dealings, non - operating assets, and the need to structure the valuation to support both succession planning and potential future transactions.`,
      uniqueConsiderations: [
        "Applied asset-based and income approaches with appropriate weighting",
        "Addressed Sharia compliance requirements in valuation methodology",
        "Incorporated family business discount factors and marketability considerations",
        "Provided detailed analysis of business segments and real estate holdings"
      ],
      outcome: {
        valueRange: "$125-140M",
        timeline: "On schedule",
        satisfaction: "100%"
      },
      clientTestimonial: "The team understood our family dynamics and cultural considerations. The valuation supported our succession planning perfectly.",
      clientRole: "Family Business Patriarch",
      clientCompany: "Established Trading Group"
    },
    {
      id: 3,
      title: "Manufacturing Company Litigation Support",
      industry: "Manufacturing",
      serviceType: "Litigation Support",
      companySize: "Large Enterprise",
      location: "Kuwait City, Kuwait",
      year: "2023",
      duration: "12 weeks",
      challenge: "Complex commercial dispute requiring expert witness testimony on business valuation and economic damages.",
      detailedChallenge: `A major manufacturing company was involved in a commercial dispute regarding a failed joint venture.The case required expert witness testimony on business valuation, lost profits calculation, and economic damages assessment.The matter was being heard in Kuwait courts with potential for international arbitration.\n\nThe engagement required detailed forensic analysis of financial records, assessment of management decisions, and calculation of economic damages under multiple scenarios.Expert testimony needed to withstand cross - examination and opposing expert challenges.`,
      uniqueConsiderations: [
        "Conducted detailed forensic analysis of financial records and management decisions",
        "Applied multiple valuation approaches to support damages calculation",
        "Prepared comprehensive expert witness report for court proceedings",
        "Provided expert testimony and withstood opposing counsel cross-examination"
      ],
      outcome: {
        valueRange: "$85M damages",
        timeline: "3 weeks early",
        satisfaction: "100%"
      },
      clientTestimonial: "The expert testimony was compelling and well-prepared. The court accepted our valuation methodology and damages calculation.",
      clientRole: "General Counsel",
      clientCompany: "Manufacturing Conglomerate"
    },
    {
      id: 4,
      title: "Real Estate Portfolio Valuation",
      industry: "Real Estate",
      serviceType: "Financial Reporting",
      companySize: "Large Enterprise",
      location: "Manama, Bahrain",
      year: "2024",
      duration: "8 weeks",
      challenge: "IFRS-compliant fair value assessment of diversified real estate portfolio for annual financial reporting.",
      detailedChallenge: `A major real estate investment company required IFRS - compliant fair value assessment of their diversified portfolio for annual financial reporting.The portfolio included commercial, residential, and mixed - use properties across multiple GCC markets.The valuation needed to meet auditor requirements and regulatory standards.\n\nChallenges included varying market conditions across different GCC markets, different property types requiring specialized expertise, and the need for quarterly updates throughout the year.`,
      uniqueConsiderations: [
        "Applied market approach with extensive comparable sales analysis",
        "Incorporated income approach for investment properties with detailed cash flow analysis",
        "Addressed market variations across different jurisdictions",
        "Provided quarterly valuation updates for ongoing financial reporting"
      ],
      outcome: {
        valueRange: "$2.1B portfolio",
        timeline: "1 week early",
        satisfaction: "95%"
      },
      clientTestimonial: "The valuation methodology was robust and met all our IFRS requirements. The quarterly updates were delivered consistently on time.",
      clientRole: "Chief Financial Officer",
      clientCompany: "Regional REIT"
    },
    {
      id: 5,
      title: "Healthcare Technology IP Valuation",
      industry: "Healthcare",
      serviceType: "IP Valuation",
      companySize: "SME",
      location: "Abu Dhabi, UAE",
      year: "2024",
      duration: "7 weeks",
      challenge: "Intellectual property valuation for healthcare technology patents and proprietary software platforms.",
      detailedChallenge: `A healthcare technology company developing AI - powered diagnostic tools required valuation of their intellectual property portfolio for licensing negotiations and potential acquisition discussions.The IP portfolio included multiple patents, proprietary algorithms, and software platforms.\n\nThe valuation required deep understanding of healthcare technology markets, regulatory approval processes, and the competitive landscape for AI - powered medical devices in the GCC region.`,
      uniqueConsiderations: [
        "Applied relief from royalty method for patent valuation",
        "Incorporated technology lifecycle and obsolescence risk factors",
        "Assessed regulatory approval pathways and market adoption rates",
        "Provided scenario analysis for different commercialization strategies"
      ],
      outcome: {
        valueRange: "$15-18M IP",
        timeline: "On schedule",
        satisfaction: "97%"
      },
      clientTestimonial: "The IP valuation was thorough and helped us structure our licensing agreements effectively. The technology expertise was impressive.",
      clientRole: "Chief Technology Officer",
      clientCompany: "HealthTech Innovator"
    },
    {
      id: 6,
      title: "Financial Services M&A Advisory",
      industry: "Financial Services",
      serviceType: "Transaction Support",
      companySize: "Large Enterprise",
      location: "Doha, Qatar",
      year: "2023",
      duration: "14 weeks",
      challenge: "Cross-border acquisition valuation for regional financial services consolidation.",
      detailedChallenge: `A major Qatari financial institution was acquiring a regional competitor to expand their GCC footprint.The transaction required comprehensive valuation analysis, due diligence support, and regulatory compliance across multiple jurisdictions.\n\nComplexities included different regulatory environments, currency considerations, integration synergies assessment, and the need to satisfy multiple stakeholder groups including regulators, shareholders, and rating agencies.`,
      uniqueConsiderations: [
        "Applied multiple valuation methodologies including DDM and P/E multiples analysis",
        "Incorporated regulatory capital requirements and Basel III compliance",
        "Assessed integration synergies and cost savings opportunities",
        "Provided fairness opinion for board and regulatory approval"
      ],
      outcome: {
        valueRange: "$750M transaction",
        timeline: "On schedule",
        satisfaction: "100%"
      },
      clientTestimonial: "The transaction support was comprehensive and professional. The valuation analysis supported successful regulatory approval.",
      clientRole: "Chief Executive Officer",
      clientCompany: "Regional Bank"
    }
  ];

  // Filter options
  const filterOptions = {
    industries: [...new Set(caseStudies.map(cs => cs.industry))],
    serviceTypes: [...new Set(caseStudies.map(cs => cs.serviceType))],
    companySizes: [...new Set(caseStudies.map(cs => cs.companySize))],
    locations: [...new Set(caseStudies.map(cs => cs.location))]
  };

  // Filtered case studies
  const filteredCaseStudies = useMemo(() => {
    return caseStudies?.filter(caseStudy => {
      // Search filter
      const matchesSearch = searchTerm === '' ||
        caseStudy?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        caseStudy?.challenge?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        caseStudy?.industry?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      // Category filters
      const matchesIndustry = activeFilters?.industry?.length === 0 ||
        activeFilters?.industry?.includes(caseStudy?.industry);

      const matchesServiceType = activeFilters?.serviceType?.length === 0 ||
        activeFilters?.serviceType?.includes(caseStudy?.serviceType);

      const matchesCompanySize = activeFilters?.companySize?.length === 0 ||
        activeFilters?.companySize?.includes(caseStudy?.companySize);

      const matchesLocation = activeFilters?.location?.length === 0 ||
        activeFilters?.location?.includes(caseStudy?.location);

      return matchesSearch && matchesIndustry && matchesServiceType &&
        matchesCompanySize && matchesLocation;
    });
  }, [searchTerm, activeFilters]);

  const handleFilterChange = (category, filters) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: filters
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      industry: [],
      serviceType: [],
      companySize: [],
      location: []
    });
    setSearchTerm('');
  };

  const handleViewDetails = (caseStudy) => {
    setSelectedCase(caseStudy);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCase(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary to-secondary text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-white/10 rounded-lg backdrop-blur-sm">
                <Icon name="FileText" size={24} className="text-white" />
              </div>
              <div className="text-sm font-medium text-white/80 uppercase tracking-wide">
                Real-World Impact
              </div>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Case Studies & Success Stories
            </h1>

            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Explore our portfolio of successful valuations across industries, showcasing
              defensible methodologies and quantified outcomes that demonstrate our expertise
              in complex financial challenges.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="secondary"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                iconSize={20}
                asChild
              >
                <Link to="/book-consultation">
                  Free Consultation
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                iconSize={20}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Download Case Study Summary
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Stats Overview */}
          <StatsOverview
            totalCases={caseStudies?.length}
            filteredCases={filteredCaseStudies?.length}
          />

          {/* Search Bar */}
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search by title, industry, or challenge..."
          />

          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Filter Panel */}
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filterOptions}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isOpen={isFilterOpen}
                onToggle={() => setIsFilterOpen(!isFilterOpen)}
              />
            </div>

            {/* Case Studies Grid */}
            <div className="lg:col-span-3">
              {filteredCaseStudies?.length === 0 ? (
                <div className="text-center py-16">
                  <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mx-auto mb-4">
                    <Icon name="Search" size={24} className="text-text-secondary" />
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    No case studies found
                  </h3>
                  <p className="text-text-secondary mb-6">
                    Try adjusting your search terms or filters to find relevant case studies.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="RefreshCw"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Clear All Filters
                  </Button>
                </div>
              ) : (
                <div className="grid gap-6">
                  {filteredCaseStudies?.map((caseStudy) => (
                    <CaseStudyCard
                      key={caseStudy?.id}
                      caseStudy={caseStudy}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="bg-muted py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mx-auto mb-6">
            <Icon name="MessageCircle" size={24} className="text-accent" />
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            Ready to Start Your Valuation Project?
          </h2>

          <p className="text-lg text-text-secondary mb-8 leading-relaxed">
            Every successful valuation starts with understanding your unique challenges.
            Let's discuss how our proven methodologies can deliver the defensible results you need.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              iconSize={20}
              asChild
            >
              <Link to="/book-consultation">
                Schedule Free Consultation
              </Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              iconSize={20}
              asChild
            >
              <a href={`tel:${CONTACT_PHONE} `}>Call {CONTACT_PHONE}</a>
            </Button>
          </div>

          <div className="mt-8 pt-8 border-t border-border">
            <div className="flex flex-wrap justify-center gap-6 text-sm text-text-secondary">
              <Link to="/services" className="hover:text-accent transition-colors duration-200">
                View All Services
              </Link>
              <Link to="/about" className="hover:text-accent transition-colors duration-200">
                Meet Our Team
              </Link>
              <Link to="/valuation" className="hover:text-accent transition-colors duration-200">
                Business Valuation
              </Link>
              <Link to="/litigation-support" className="hover:text-accent transition-colors duration-200">
                Litigation Support
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Case Study Modal */}
      <CaseStudyModal
        caseStudy={selectedCase}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
      <Footer />
    </div>
  );
};

export default CaseStudies;